import { ElMessage } from 'element-plus'
import { defineStore, storeToRefs } from 'pinia'
import { nextTick } from 'vue'

import EventBus, { GlobalEvents, VideoCallEvents } from '@shared/event-bus'
import { Namespaces } from '@shared/models/enums/namespaces'
import { RoomState } from '@shared/models/enums/twilio-video-room'
import { DataMessageTypes } from '@shared/models/interfaces/quick-messages'
import { resourcePath } from '@shared/plugins/api'
import { ApiTransport } from '@shared/plugins/api-transport'
import { useVideoCallCompatibilityStore } from '@shared/store/video-call-compatibility'
import {
  room,
  useVideoCallConfigsStore,
} from '@shared/store/video-call-configs'
import { isMobileDevice } from '@shared/utils/helpers/is-mobile-device'
import { lsGet, LsGlobalKeys, lsHas } from '@shared/utils/helpers/local-storage'
import { rtcSessionValid } from '@shared/utils/helpers/video-call'
import { VideoCallSession } from '@shared/utils/models/video-call-config'

import { MessageType } from '@protos/chat/message_type_pb'

import i18n from '@pc/i18n'
import { TwilioDeclineDTO, TwilioJoinDTO } from '@pc/models/proto/dto/twilio'
import { TwilioSessionRO } from '@pc/models/proto/ro/twilio'
import { useConversationsStore } from '@pc/store/conversations'
import { useMessagesStore } from '@pc/store/messages'
import { useRtcSessionsStore } from '@pc/store/rtc-sessions'
import { useSocketStore } from '@pc/store/socket'

type State = {
  isCallIncomingShown: boolean
  isFeedbackDialogOpened: boolean
}

const state = (): State => ({
  isCallIncomingShown: false,
  isFeedbackDialogOpened: false,
})

export const apiTransport = new ApiTransport(resourcePath, 'pc')

export const useVideoCallStore = defineStore('video-chat', {
  state,
  actions: {
    async restoreVideoCallFromCache() {
      const isCachedVideoCall = lsHas(LsGlobalKeys.CurrentVideoCall)
      if (!isCachedVideoCall) {
        return
      }

      const rtcSessionsStore = useRtcSessionsStore()
      const videoCallConfigsStore = useVideoCallConfigsStore()

      const videoCallSession: VideoCallSession = JSON.parse(
        lsGet(LsGlobalKeys.CurrentVideoCall)
      )

      const session = await rtcSessionsStore.fetchRtcSession({
        roomId: videoCallSession.roomId ?? '',
      })

      if (!rtcSessionValid(session.data)) {
        videoCallConfigsStore.cleanCachedVideoConversation()
        return
      }

      await this.start()
    },

    async start() {
      const videoCallConfigsStore = useVideoCallConfigsStore()

      const requestedAt = new Date().toISOString()
      const isCachedVideoCall = lsHas(LsGlobalKeys.CurrentVideoCall)

      videoCallConfigsStore.isVideoChatMode = true
      videoCallConfigsStore.isLoading = true

      try {
        if (room?.state && room.state !== RoomState.Disconnected) {
          // This video call is already going, we just need to rehydrate it
          await nextTick()
          return videoCallConfigsStore.reattachAllTracks()
        }

        const videoCallCompatibilityStore = useVideoCallCompatibilityStore()
        const { reasonForFailure } = storeToRefs(videoCallCompatibilityStore)

        await videoCallCompatibilityStore.ensureCompatabilityIsFetched(
          Namespaces.PC
        )

        if (reasonForFailure.value) {
          videoCallConfigsStore.compatibilityError = reasonForFailure.value
          videoCallConfigsStore.isLoading = false
          return
        }

        if (!isCachedVideoCall) {
          if (!videoCallConfigsStore.roomId) {
            ElMessage.error(
              i18n.global.t('pt.messages.errors.unableToJoinCall')
            )
            /* eslint-disable-next-line no-console */
            console.error('Error roomId is null')
            videoCallConfigsStore.isLoading = false
            return
          }

          const payload = {
            requestedAt,
          }
          const joinParams = {
            roomId: videoCallConfigsStore.roomId || '',
            data: payload,
          }

          const { data } = (await apiTransport.post(
            '/twilio/join',
            new TwilioJoinDTO(joinParams),
            TwilioJoinDTO,
            TwilioSessionRO
          )) ?? { data: {} }

          if (data) {
            videoCallConfigsStore.$patch(data)
          }
        }
        const shouldStartScreenShare = !isMobileDevice()
        await videoCallConfigsStore.initializeTracks(shouldStartScreenShare)
        videoCallConfigsStore.applyRoomEventListeners({
          namespace: Namespaces.PC,
        })
        videoCallConfigsStore.initializeParticipants()
      } catch (e) {
        videoCallConfigsStore.cleanCachedVideoConversation()

        videoCallConfigsStore.isVideoChatMode = false
        videoCallConfigsStore.disconnectFromRoom()
        /* eslint-disable-next-line no-console */
        console.log('Error during starting a video call', e)
        throw e
      } finally {
        if (videoCallConfigsStore.mediaError) {
          const socketStore = useSocketStore()

          socketStore.pushVideoCallCancel(
            videoCallConfigsStore.conversation?.id ?? 0
          )
        }
        videoCallConfigsStore.isLoading = false
      }
    },

    onHangUp() {
      const videoCallConfigsStore = useVideoCallConfigsStore()

      videoCallConfigsStore.sendDataTrackMessage({
        type: DataMessageTypes.HangUp,
        payload: null,
      })

      videoCallConfigsStore.disconnectFromRoom()
      videoCallConfigsStore.closeVideoConversation()
      if (room) {
        videoCallConfigsStore.removeParticipantTracks(room.localParticipant)
      }

      EventBus.emit(VideoCallEvents.RedirectToConversation)
    },

    async declineCall() {
      const videoCallConfigsStore = useVideoCallConfigsStore()

      const payload = {
        roomId: videoCallConfigsStore.roomId || '',
        data: {
          epochTime: Math.ceil(new Date().getTime() / 1000),
          createdAt: new Date().toISOString(),
        },
      }

      await apiTransport.post(
        '/twilio/decline',
        new TwilioDeclineDTO(payload),
        TwilioDeclineDTO,
        undefined,
        {
          onUploadProgress({ total, loaded }) {
            EventBus.emit(GlobalEvents.AsyncRequestProgress, {
              total,
              loaded,
            })
          },
        }
      )
    },

    async acceptCall() {
      const conversationsStore = useConversationsStore()
      const messagesStore = useMessagesStore()
      const videoCallConfigsStore = useVideoCallConfigsStore()

      if (!conversationsStore.currentConversation) {
        return
      }

      videoCallConfigsStore.conversation =
        conversationsStore.currentConversation

      this.start()
      const messageType = videoCallConfigsStore.isScreenSharingMode
        ? MessageType.SCREENSHARING_ACCEPT
        : MessageType.VIDEO_CHAT_ACCEPT

      await messagesStore.sendMessage(
        {
          text: 'Yes',
          messageType,
          attachments: [],
          conversationId: conversationsStore.currentConversation.id,
        },
        []
      )
    },
  },
})
